import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bot,
  Building2,
  CheckSquare,
  Copy,
  ExternalLink,
  FileText,
  FolderOpen,
  Globe,
  MessageSquare,
  Receipt,
  User,
  type LucideIcon,
} from 'lucide-react';
import {
  formatDuration,
  formatRelativeDate,
  MANAGED_TABLE_CONFIGS,
  type Client,
  type ManagedTableColumn,
  type ManagedTableKey,
  type ManagedTableRecord,
  type Proposal,
  type Task,
  type User as UserRecord,
  type Website,
} from '@oneup/shared';
import {
  clientsApi,
  communicationsApi,
  managedTablesApi,
  proposalsApi,
  tasksApi,
  usersApi,
  websitesApi,
  type CommunicationTimelineItem,
} from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MarkdownNotes } from '@/components/markdown-notes';
import { RecordHomebaseShell, RelatedRecordTabs } from '@/components/workspace';
import { cn } from '@/lib/utils';
import { pushRecent } from '@/lib/recents';
import { useUiStore } from '@/stores/ui';
import { taskStatusVariant } from '@/lib/task-status';
import { TaskDrawer } from './TaskDrawer';

interface OrganizationHomebaseProps {
  clientId: string;
  onClose: () => void;
  listTitle?: string;
  selectedIndex?: number;
  totalRecords?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  onOpenTask?: (id: string) => void;
  onOpenWebsite?: (id: string) => void;
  onOpenUser?: (id: string) => void;
}

type DriveMode = 'grid' | 'list';
type HomebaseTab =
  | 'overview'
  | 'projects'
  | 'websites'
  | 'agents'
  | 'team'
  | 'communications'
  | 'files'
  | 'billing'
  | 'usage';

interface BusinessInfoDraft {
  name: string;
  status: string;
  relationshipType: string;
  website: string;
  notes: string;
}

export function OrganizationHomebase({
  clientId,
  onClose,
  listTitle = 'Organizations',
  selectedIndex,
  totalRecords,
  onPrevious,
  onNext,
  onOpenTask,
  onOpenWebsite,
  onOpenUser,
}: OrganizationHomebaseProps) {
  const query = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => clientsApi.get(clientId),
  });

  useEffect(() => {
    if (!query.data) return;
    pushRecent({
      type: 'client',
      id: query.data.id,
      name: query.data.name,
      secondary: query.data.status,
    });
  }, [query.data]);

  if (query.isLoading) return <HomebaseLoading />;

  if (query.error || !query.data) {
    return (
      <Card className="px-4 py-6 text-sm text-destructive">
        Could not load organization: {query.error instanceof Error ? query.error.message : 'Unknown error'}
      </Card>
    );
  }

  return (
    <OrganizationHomebaseBody
      client={query.data}
      onClose={onClose}
      listTitle={listTitle}
      selectedIndex={selectedIndex}
      totalRecords={totalRecords}
      onPrevious={onPrevious}
      onNext={onNext}
      onOpenTask={onOpenTask}
      onOpenWebsite={onOpenWebsite}
      onOpenUser={onOpenUser}
    />
  );
}

function OrganizationHomebaseBody({
  client,
  onClose,
  listTitle,
  selectedIndex,
  totalRecords,
  onPrevious,
  onNext,
  onOpenTask,
  onOpenWebsite,
  onOpenUser,
}: {
  client: Client;
  onClose: () => void;
  listTitle: string;
  selectedIndex?: number;
  totalRecords?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  onOpenTask?: (id: string) => void;
  onOpenWebsite?: (id: string) => void;
  onOpenUser?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const [isEditing, setIsEditing] = useState(false);
  const [nestedTaskId, setNestedTaskId] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState<HomebaseTab>('overview');
  const [projectsIncludeClosed, setProjectsIncludeClosed] = useState(false);
  const [draft, setDraft] = useState({
    name: client.name,
    status: client.status ?? '',
    relationshipType: client.relationshipType ?? '',
    website: client.website ?? '',
    notes: client.notes ?? '',
  });

  useEffect(() => {
    setDraft({
      name: client.name,
      status: client.status ?? '',
      relationshipType: client.relationshipType ?? '',
      website: client.website ?? '',
      notes: client.notes ?? '',
    });
  }, [client.id, client.name, client.notes, client.relationshipType, client.status, client.website]);

  const saveMutation = useMutation({
    mutationFn: () => clientsApi.update(client.id, draft),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      setNotice('Organization updated');
      setIsEditing(false);
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Save failed', 'destructive'),
  });

  const projectsEnabled = activeTab === 'projects';
  const websitesEnabled = activeTab === 'websites';
  const agentsEnabled = activeTab === 'agents';
  const teamEnabled = activeTab === 'team';
  const communicationsEnabled = activeTab === 'communications';
  const billingEnabled = activeTab === 'billing';

  const projectsQuery = useQuery({
    queryKey: ['tasks', { clientId: client.id, includeClosed: projectsIncludeClosed, homebase: true }],
    queryFn: () =>
      tasksApi.list({
        clientId: client.id,
        includeClosed: projectsIncludeClosed,
        limit: 300,
      }),
    enabled: Boolean(client.id) && projectsEnabled,
    staleTime: 60_000,
  });
  const websitesQuery = useQuery({
    queryKey: ['websites', { clientId: client.id, homebase: true }],
    queryFn: () => websitesApi.list({ clientId: client.id }),
    enabled: Boolean(client.id) && websitesEnabled,
    staleTime: 60_000,
  });
  const usersQuery = useQuery({
    queryKey: ['users', { clientId: client.id, homebase: true }],
    queryFn: () => usersApi.list({ clientId: client.id }),
    enabled: Boolean(client.id) && teamEnabled,
    staleTime: 60_000,
  });
  const communicationsQuery = useQuery({
    queryKey: ['communications', { clientId: client.id, homebase: true }],
    queryFn: () => communicationsApi.list({ clientId: client.id, limit: 100 }),
    enabled: Boolean(client.id) && communicationsEnabled,
    staleTime: 60_000,
  });
  const proposalsQuery = useQuery({
    queryKey: ['proposals', { clientId: client.id, recordIds: client.proposalIds?.join(',') ?? '', homebase: true }],
    queryFn: () =>
      proposalsApi.list({
        recordIds: client.proposalIds,
        limit: Math.max(client.proposalIds?.length ?? 0, 1),
      }),
    enabled: Boolean(client.id) && billingEnabled && Boolean(client.proposalIds?.length),
    staleTime: 60_000,
  });
  const agentsQuery = useManagedRecords('agents', client.agentIds, agentsEnabled);
  const subscriptionsQuery = useManagedRecords('subscriptions', client.subscriptionIds, billingEnabled);
  const invoicesQuery = useManagedRecords('invoices', client.invoiceIds, billingEnabled);
  const paymentsQuery = useManagedRecords('payments', client.paymentIds, billingEnabled);
  const timeEntriesQuery = useManagedRecords('timeEntries', client.timeEntryIds, billingEnabled);

  const projects = projectsQuery.data?.items ?? [];
  const websites = websitesQuery.data?.items ?? [];
  const users = usersQuery.data?.items ?? [];
  const communicationItems = communicationsQuery.data?.items ?? [];
  const proposals = proposalsQuery.data?.items ?? [];
  const usageVisible = hasUsageData(client);

  return (
    <>
      <RecordHomebaseShell
        title={client.name}
        recordType={listTitle}
        backLabel="Back to organizations"
        onClose={onClose}
        canGoPrevious={typeof selectedIndex === 'number' && selectedIndex > 0}
        canGoNext={
          typeof selectedIndex === 'number' &&
          typeof totalRecords === 'number' &&
          selectedIndex >= 0 &&
          selectedIndex < totalRecords - 1
        }
        onPrevious={onPrevious}
        onNext={onNext}
        actions={
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              {client.status && <Badge variant="muted">{client.status}</Badge>}
              {client.relationshipTypes?.map((type) => (
                <Badge key={type} variant="outline">
                  {type}
                </Badge>
              ))}
              {client.lastModifiedAt && <span>Updated {formatRelativeDate(client.lastModifiedAt)}</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditing((value) => !value)}>
                {isEditing ? 'Cancel Edit' : 'Edit Business Info'}
              </Button>
              {isEditing && (
                <Button
                  size="sm"
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending || draft.name.trim().length === 0}
                >
                  {saveMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          {isEditing && (
            <EditBusinessInfoCard
              draft={draft}
              onChange={(next) => setDraft((current) => ({ ...current, ...next }))}
            />
          )}

          <section className="grid gap-4 xl:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
            <ProfileCard client={client} />
            <BusinessInfoPanel client={client} />
          </section>

          <RelatedRecordTabs>
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as HomebaseTab)}
              className="space-y-3"
            >
              <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="projects">Projects</TabsTrigger>
                <TabsTrigger value="websites">Websites</TabsTrigger>
                <TabsTrigger value="agents">Agents</TabsTrigger>
                <TabsTrigger value="team">Team</TabsTrigger>
                <TabsTrigger value="communications">Communications</TabsTrigger>
                <TabsTrigger value="files">Files / Drive</TabsTrigger>
                <TabsTrigger value="billing">Billing / Subscriptions</TabsTrigger>
                {usageVisible && <TabsTrigger value="usage">Usage / Wallets</TabsTrigger>}
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <OverviewTab
                  client={client}
                  projectCount={countRecords(client.projectIds)}
                  websiteCount={countRecords(client.websiteIds)}
                  agentCount={countRecords(client.agentIds)}
                  teamCount={countRecords(client.contactIds) || countRecords(client.contactNames)}
                  communicationCount={countRecords(client.quickNoteSubmissionIds)}
                  invoiceCount={countRecords(client.invoiceIds)}
                  subscriptionCount={countRecords(client.subscriptionIds)}
                />
              </TabsContent>

              <TabsContent value="projects">
                <ProjectsTab
                  tasks={projects}
                  isLoading={projectsQuery.isLoading}
                  error={projectsQuery.error}
                  onOpenTask={onOpenTask}
                  onQuickView={setNestedTaskId}
                  clientId={client.id}
                  displayClosed={projectsIncludeClosed}
                  onDisplayClosedChange={setProjectsIncludeClosed}
                  onNewProject={() =>
                    openCreateDrawer({
                      type: 'task',
                      values: { clientId: client.id, clientName: client.name },
                    })
                  }
                />
              </TabsContent>

              <TabsContent value="websites">
                <WebsitesTab
                  websites={websites}
                  isLoading={websitesQuery.isLoading}
                  error={websitesQuery.error}
                  onOpenWebsite={onOpenWebsite}
                  onNewWebsite={() => openCreateDrawer({ type: 'website' })}
                />
              </TabsContent>

              <TabsContent value="agents">
                <AgentsTab
                  records={agentsQuery.data?.items ?? []}
                  isLoading={agentsQuery.isLoading}
                  error={agentsQuery.error}
                  onOpen={(id) => navigate(`/ai-systems/agents/${id}`)}
                />
              </TabsContent>

              <TabsContent value="team">
                <TeamTab
                  users={users}
                  isLoading={usersQuery.isLoading}
                  error={usersQuery.error}
                  onOpenUser={onOpenUser}
                  onNewUser={() => openCreateDrawer({ type: 'contact' })}
                />
              </TabsContent>

              <TabsContent value="communications">
                <CommunicationsTab
                  items={communicationItems}
                  isLoading={communicationsQuery.isLoading}
                  error={communicationsQuery.error}
                />
              </TabsContent>

              <TabsContent value="files">
                <DriveSection client={client} framed={false} />
              </TabsContent>

              <TabsContent value="billing" className="space-y-4">
                <BillingTab
                  subscriptions={subscriptionsQuery.data?.items ?? []}
                  invoices={invoicesQuery.data?.items ?? []}
                  payments={paymentsQuery.data?.items ?? []}
                  proposals={proposals}
                  timeEntries={timeEntriesQuery.data?.items ?? []}
                  loading={
                    subscriptionsQuery.isLoading ||
                    invoicesQuery.isLoading ||
                    paymentsQuery.isLoading ||
                    proposalsQuery.isLoading ||
                    timeEntriesQuery.isLoading
                  }
                  onOpenSubscription={(id) => navigate(`/billing/subscriptions/${id}`)}
                  onOpenInvoice={(id) => navigate(`/billing/invoices/${id}`)}
                  onOpenPayment={(id) => navigate(`/billing/payments/${id}`)}
                  onOpenProposal={(id) => navigate(`/proposals/${id}`)}
                  onOpenTimeEntry={(id) => navigate(`/work/time-entries/${id}`)}
                />
              </TabsContent>

              {usageVisible && (
                <TabsContent value="usage">
                  <UsageTab client={client} />
                </TabsContent>
              )}
            </Tabs>
          </RelatedRecordTabs>
        </div>
      </RecordHomebaseShell>
      {nestedTaskId && <TaskDrawer taskId={nestedTaskId} onClose={() => setNestedTaskId(undefined)} />}
    </>
  );
}

function useManagedRecords(tableKey: ManagedTableKey, recordIds: string[] | undefined, enabled: boolean) {
  const ids = recordIds ?? [];
  return useQuery({
    queryKey: ['managed-table', tableKey, { recordIds: ids.join(',') }],
    queryFn: () => managedTablesApi.list(tableKey, { recordIds: ids, limit: Math.max(ids.length, 1) }),
    enabled: enabled && ids.length > 0,
    staleTime: 60_000,
  });
}

function HomebaseLoading() {
  return (
    <Card className="space-y-4 p-5">
      <div className="h-5 w-64 animate-pulse rounded bg-muted" />
      <div className="grid gap-4 xl:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
        <div className="h-72 animate-pulse rounded-lg bg-muted/60" />
        <div className="h-72 animate-pulse rounded-lg bg-muted/50" />
      </div>
      <div className="h-80 animate-pulse rounded-lg bg-muted/40" />
    </Card>
  );
}

function EditBusinessInfoCard({
  draft,
  onChange,
}: {
  draft: BusinessInfoDraft;
  onChange: (patch: Partial<BusinessInfoDraft>) => void;
}) {
  return (
    <Card className="space-y-3 p-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Company Name">
          <Input value={draft.name} onChange={(event) => onChange({ name: event.target.value })} />
        </Field>
        <Field label="Status">
          <Input value={draft.status} onChange={(event) => onChange({ status: event.target.value })} />
        </Field>
        <Field label="Relationship">
          <Input
            value={draft.relationshipType}
            onChange={(event) => onChange({ relationshipType: event.target.value })}
          />
        </Field>
        <Field label="Website">
          <Input value={draft.website} onChange={(event) => onChange({ website: event.target.value })} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          value={draft.notes}
          onChange={(event) => onChange({ notes: event.target.value })}
          rows={4}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </Field>
    </Card>
  );
}

function ProfileCard({ client }: { client: Client }) {
  const driveUrl = driveFolderUrl(client);
  const fullAddress = organizationAddress(client);
  const hasContactDetails = Boolean(
    client.orgContactEmail || client.businessPhoneNumber || client.website || fullAddress,
  );
  return (
    <Card className="flex min-h-[320px] flex-col justify-between gap-5 p-5">
      <div className="space-y-5">
        <div className="flex min-h-24 items-center justify-center rounded-lg border border-border bg-muted/30 p-4">
          {client.logoUrl ? (
            <img src={client.logoUrl} alt="" className="max-h-28 max-w-full object-contain" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-9 w-9" aria-hidden />
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div>
            <h2 className="text-[22px] font-semibold leading-tight tracking-normal">{client.name}</h2>
            {client.businessShortDescription && (
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                {client.businessShortDescription}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {client.status && <Badge variant="muted">{client.status}</Badge>}
            {client.industryTypes?.map((type) => (
              <Badge key={type} variant="outline">
                {type}
              </Badge>
            ))}
            {client.relationshipTypes?.map((type) => (
              <Badge key={type} variant="outline">
                {type}
              </Badge>
            ))}
          </div>
          <ContactChips names={client.contactNames} />
        </div>
        {hasContactDetails && (
          <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
            <CompactContactRow label="Contact Email" value={client.orgContactEmail} kind="email" />
            <CompactContactRow label="Phone Number" value={client.businessPhoneNumber} kind="phone" />
            <CompactContactRow
              label="Website"
              value={client.website}
              href={client.website ? normalizeUrl(client.website) : undefined}
            />
            <CompactContactRow label="Full Address" value={fullAddress} />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {client.orgContactEmail && (
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <a href={`mailto:${client.orgContactEmail}`}>Email Client</a>
          </Button>
        )}
        {client.businessPhoneNumber && (
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <a href={`tel:${client.businessPhoneNumber}`}>Call Client</a>
          </Button>
        )}
        {client.website && (
          <Button asChild size="sm" className="gap-1.5">
            <a href={normalizeUrl(client.website)} target="_blank" rel="noopener noreferrer">
              Open Website
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </Button>
        )}
        {driveUrl && (
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <a href={driveUrl} target="_blank" rel="noopener noreferrer">
              Open Drive
              <FolderOpen className="h-3.5 w-3.5" aria-hidden />
            </a>
          </Button>
        )}
      </div>
    </Card>
  );
}

function BusinessInfoPanel({ client }: { client: Client }) {
  return (
    <Card className="p-0">
      <div className="divide-y divide-border">
        <InfoRow label="Service Area" value={client.serviceArea} multiline />
        <InfoRow label="Short Description" value={client.businessShortDescription} multiline />
        <InfoRow label="Services Description" value={client.servicesDescription} multiline />
        <InfoRow label="Core Business Summary" value={client.coreBusinessSummary} multiline />
        <ExpandableInfoRow label="Frequently Asked Questions" value={client.frequentlyAskedQuestions} />
        <ExpandableInfoRow label="Notes" value={client.notes} />
        <div className="grid gap-0 md:grid-cols-2">
          <InfoRow label="Time Zone" value={client.timeZone} compact />
          <InfoRow label="Place ID" value={client.placeId} compact copyValue={client.placeId} />
        </div>
      </div>
    </Card>
  );
}

function OverviewTab({
  client,
  projectCount,
  websiteCount,
  agentCount,
  teamCount,
  communicationCount,
  invoiceCount,
  subscriptionCount,
}: {
  client: Client;
  projectCount: number;
  websiteCount: number;
  agentCount: number;
  teamCount: number;
  communicationCount: number;
  invoiceCount: number;
  subscriptionCount: number;
}) {
  const warnings = setupWarnings(client, subscriptionCount);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-5">
        <SummaryStat label="Projects" value={String(projectCount)} icon={CheckSquare} />
        <SummaryStat label="Websites" value={String(websiteCount)} icon={Globe} />
        <SummaryStat label="Agents" value={String(agentCount)} icon={Bot} />
        <SummaryStat label="Team" value={String(teamCount)} icon={User} />
        <SummaryStat
          label="Communications"
          value={String(client.openSupportCount ?? communicationCount)}
          icon={MessageSquare}
        />
      </div>
      <Card className="grid gap-4 p-4 lg:grid-cols-3">
        <div>
          <SectionTitle>Account Snapshot</SectionTitle>
          <FactList
            facts={[
              ['Status', client.status],
              ['Communications', String(communicationCount)],
              ['Open Support', formatNumber(client.openSupportCount)],
              ['Invoices', String(invoiceCount)],
              ['Subscriptions', String(subscriptionCount)],
              ['Agents', String(agentCount)],
            ]}
          />
        </div>
        <div>
          <SectionTitle>Account Details</SectionTitle>
          <FactList
            facts={[
              ['Created', formatOptionalDate(client.createdAt)],
              ['Last Modified', formatOptionalDate(client.lastModifiedAt)],
              ['Billing Contact', client.billingContactName],
              ['Billing Emails', client.allBillingEmails?.join(', ')],
              ['Stripe Customer', client.stripeCustomerId],
              ['Stripe Customer URL', client.stripeCustomerUrl],
              ['Voice Agent Licenses', formatNumber(client.voiceAgentLicenses)],
            ]}
          />
        </div>
        <div>
          <SectionTitle>Setup Warnings</SectionTitle>
          {warnings.length === 0 ? (
            <div className="text-[12px] text-muted-foreground">No obvious setup gaps.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {warnings.map((warning) => (
                <Badge key={warning} variant="warning" className="gap-1">
                  <AlertTriangle className="h-3 w-3" aria-hidden />
                  {warning}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </Card>
      <Card className="p-4">
        <SectionTitle>Relationships</SectionTitle>
        <FactList
          facts={[
            ['Parent Organization', client.parentOrganizationName ?? client.parentOrganizationId],
            ['Child Organizations', formatCount(client.childOrganizationIds, 'organization')],
            ['Webmaster', client.webmasterEmails?.join(', ') ?? formatCount(client.webmasterIds, 'webmaster')],
            ['Contacts', client.contactNames?.join(', ') ?? formatCount(client.contactIds, 'contact')],
            ['Leads', formatCount(client.leadIds, 'lead')],
          ]}
        />
      </Card>
    </div>
  );
}

function ProjectsTab({
  tasks,
  isLoading,
  error,
  onOpenTask,
  onQuickView,
  clientId,
  displayClosed,
  onDisplayClosedChange,
  onNewProject,
}: {
  tasks: Task[];
  isLoading: boolean;
  error: unknown;
  onOpenTask?: (id: string) => void;
  onQuickView: (id: string) => void;
  clientId: string;
  displayClosed: boolean;
  onDisplayClosedChange: (value: boolean) => void;
  onNewProject: () => void;
}) {
  const openTasks = useMemo(() => tasks.filter((task) => !isClosedTask(task)), [tasks]);
  const visibleTasks = useMemo(
    () => (displayClosed ? tasks : openTasks).slice(0, 6),
    [displayClosed, openTasks, tasks],
  );
  const hiddenClosedCount = tasks.length - openTasks.length;

  if (isLoading) return <PanelLoading />;
  if (error) return <PanelError label="projects" error={error} />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-[16px] font-semibold">Projects</h2>
          {!displayClosed ? (
            <p className="text-[12px] text-muted-foreground">
              Showing open projects. Enable Display Closed to load completed work.
            </p>
          ) : hiddenClosedCount > 0 ? (
            <p className="text-[12px] text-muted-foreground">
              Including {hiddenClosedCount} closed project{hiddenClosedCount === 1 ? '' : 's'}.
            </p>
          ) : (
            <p className="text-[12px] text-muted-foreground">Closed projects are included.</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-[12px] text-muted-foreground">
            <input
              type="checkbox"
              checked={displayClosed}
              onChange={(event) => onDisplayClosedChange(event.target.checked)}
              className="h-3.5 w-3.5 accent-primary"
            />
            Display Closed
          </label>
          <Button asChild size="sm" variant="outline">
            <a href={`/tasks?clientId=${encodeURIComponent(clientId)}`}>View All</a>
          </Button>
          <Button size="sm" onClick={onNewProject}>
            New Project
          </Button>
        </div>
      </div>
      {tasks.length === 0 ? (
        <EmptyPanel>{displayClosed ? 'No linked projects.' : 'No open linked projects.'}</EmptyPanel>
      ) : visibleTasks.length === 0 ? (
        <EmptyPanel>No open linked projects. Use Display Closed or View All to see completed work.</EmptyPanel>
      ) : (
        <>
          <div className="grid gap-3 xl:grid-cols-3">
            {visibleTasks.map((task) => (
              <Card key={task.id} className="flex min-h-44 flex-col justify-between p-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {task.status && <Badge variant={taskStatusVariant(task.semanticStatus)}>{task.status}</Badge>}
                    {task.priority && <Badge variant="outline">{task.priority}</Badge>}
                  </div>
                  <h3 className="line-clamp-2 text-[15px] font-semibold">{task.name}</h3>
                  <FactList
                    facts={[
                      ['Target Date', formatOptionalDate(task.targetDate)],
                      ['Start Date', formatOptionalDate(task.startDate)],
                      ['Estimate', formatDuration(task.estimateMinutes)],
                      ['Tracked', formatDuration(task.trackedMinutes)],
                      ['Next Action', task.nextAction],
                      ['Definition of Done', task.definitionOfDone],
                      ['Question for Clients', task.questionForClients],
                    ]}
                    compact
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <Button size="sm" className="flex-1" onClick={() => onQuickView(task.id)}>
                    Quick View
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => onOpenTask?.(task.id)}>
                    Open Page
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          {(displayClosed ? tasks : openTasks).length > visibleTasks.length && (
            <p className="text-[12px] text-muted-foreground">
              Showing {visibleTasks.length} of {(displayClosed ? tasks : openTasks).length}. Use View All for the
              full project list.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function WebsitesTab({
  websites,
  isLoading,
  error,
  onOpenWebsite,
  onNewWebsite,
}: {
  websites: Website[];
  isLoading: boolean;
  error: unknown;
  onOpenWebsite?: (id: string) => void;
  onNewWebsite: () => void;
}) {
  if (isLoading) return <PanelLoading />;
  if (error) return <PanelError label="websites" error={error} />;

  return (
    <div className="space-y-3">
      <TabHeader title="Websites" actionLabel="New Website" onAction={onNewWebsite} />
      {websites.length === 0 ? (
        <EmptyPanel>No linked websites.</EmptyPanel>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {websites.map((website) => (
            <Card key={website.id} className="overflow-hidden">
              {website.screenshotUrl && (
                <img src={website.screenshotUrl} alt="" className="h-40 w-full object-cover object-top" />
              )}
              <div className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-[15px] font-semibold">
                      {website.friendlyName ?? website.url}
                    </h3>
                    {website.url && <p className="truncate text-[12px] text-muted-foreground">{website.url}</p>}
                  </div>
                  {website.status && <Badge variant="muted">{website.status}</Badge>}
                </div>
                <FactList
                  facts={[
                    ['Type', website.type],
                    ['Hosting Platform', website.hostingPlatform],
                    ['Who Hosts', website.hostedBy],
                    ['Purpose', website.notes],
                    ['Last Captured', formatOptionalDate(website.lastModifiedAt)],
                  ]}
                  compact
                />
                <div className="flex flex-wrap gap-2">
                  {website.url && (
                    <Button asChild size="sm">
                      <a href={normalizeUrl(website.url)} target="_blank" rel="noopener noreferrer">
                        Open Website
                      </a>
                    </Button>
                  )}
                  {website.wpAdminUrl && (
                    <Button asChild size="sm" variant="outline">
                      <a href={normalizeUrl(website.wpAdminUrl)} target="_blank" rel="noopener noreferrer">
                        Open WP Admin
                      </a>
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => onOpenWebsite?.(website.id)}>
                    Open Details
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentsTab({
  records,
  isLoading,
  error,
  onOpen,
}: {
  records: ManagedTableRecord[];
  isLoading: boolean;
  error: unknown;
  onOpen: (id: string) => void;
}) {
  if (isLoading) return <PanelLoading />;
  if (error) return <PanelError label="agents" error={error} />;

  return (
    <div className="space-y-3">
      <TabHeader title="Agents" icon={Bot} />
      {records.length === 0 ? (
        <EmptyPanel>No linked agents.</EmptyPanel>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {records.map((record) => {
            const name = managedField(record, 'Agent Name') ?? record.id;
            const avatar = imageUrlFromValue(record.fields['Agent Avatar']);
            const dashboardUrl = managedField(record, 'Vapi Dashboard Link');
            return (
              <Card key={record.id} className="p-4">
                <div className="flex gap-3">
                  <Avatar name={name} imageUrl={avatar} />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="truncate text-[15px] font-semibold">{name}</h3>
                        {managedField(record, 'Agent Role') && (
                          <p className="text-[12px] text-muted-foreground">{managedField(record, 'Agent Role')}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {managedField(record, 'Agent Profile Status') && (
                          <Badge variant="muted">{managedField(record, 'Agent Profile Status')}</Badge>
                        )}
                        {managedField(record, 'Connection Status') && (
                          <Badge variant="outline">{managedField(record, 'Connection Status')}</Badge>
                        )}
                      </div>
                    </div>
                    <FactList
                      facts={[
                        ['Agent Type', managedField(record, 'Agent Type')],
                        ['Voice Type', managedField(record, 'Voice Type')],
                        ['Phone', managedField(record, 'Agent Phone Number')],
                        ['Vapi Agent ID', managedField(record, 'vapi_agent_id')],
                      ]}
                      compact
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => onOpen(record.id)}>
                        Open Agent Details
                      </Button>
                      {dashboardUrl && (
                        <Button asChild size="sm" variant="outline">
                          <a href={normalizeUrl(dashboardUrl)} target="_blank" rel="noopener noreferrer">
                            Vapi Dashboard
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TeamTab({
  users,
  isLoading,
  error,
  onOpenUser,
  onNewUser,
}: {
  users: UserRecord[];
  isLoading: boolean;
  error: unknown;
  onOpenUser?: (id: string) => void;
  onNewUser: () => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) =>
      [user.fullName, user.email, user.phone, user.title].some((value) =>
        (value ?? '').toLowerCase().includes(q),
      ),
    );
  }, [search, users]);

  if (isLoading) return <PanelLoading />;
  if (error) return <PanelError label="team" error={error} />;

  return (
    <div className="space-y-3">
      <TabHeader title="Team" actionLabel="New User Request" onAction={onNewUser} />
      <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search team" />
      {filtered.length === 0 ? (
        <EmptyPanel>No linked team members.</EmptyPanel>
      ) : (
        <div className="space-y-2">
          {filtered.map((user) => (
            <Card key={user.id} className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
              <Avatar name={user.fullName} imageUrl={user.profileImageUrl} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[15px] font-semibold">{user.fullName}</h3>
                  {user.status && <Badge variant="muted">{user.status}</Badge>}
                  {user.userTypes?.map((type) => (
                    <Badge key={type} variant="outline">
                      {type}
                    </Badge>
                  ))}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
                  {user.title && <span>{user.title}</span>}
                  {user.email && <span>{user.email}</span>}
                  {user.phone && <span>{user.phone}</span>}
                  {user.createdAt && <span>Created {formatRelativeDate(user.createdAt)}</span>}
                  {user.lastSeenAt && <span>Last seen {formatRelativeDate(user.lastSeenAt)}</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {user.email && (
                  <Button asChild size="sm" variant="outline">
                    <a href={`mailto:${user.email}`}>Email</a>
                  </Button>
                )}
                {user.email && <CopyButton value={user.email} label="Email" />}
                <Button size="sm" onClick={() => onOpenUser?.(user.id)}>
                  Open User Details
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CommunicationsTab({
  items,
  isLoading,
  error,
}: {
  items: CommunicationTimelineItem[];
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) return <PanelLoading />;
  if (error) return <PanelError label="communications" error={error} />;

  return (
    <div className="space-y-3">
      <TabHeader title="Communications" icon={MessageSquare} />
      {items.length === 0 ? (
        <EmptyPanel>No linked communications.</EmptyPanel>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-[14px] font-semibold">{item.title}</h3>
                    <Badge variant="outline">{item.sourceLabel}</Badge>
                    {item.status && <Badge variant="muted">{item.status}</Badge>}
                    {item.tags.map((tag) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  {item.summary && (
                    <p className="mt-2 line-clamp-3 text-[13px] leading-relaxed text-muted-foreground">
                      {item.summary}
                    </p>
                  )}
                </div>
                <span className="text-[12px] text-muted-foreground">{formatRelativeDate(item.occurredAt)}</span>
              </div>
              <FactList
                facts={[
                  ['Submitted By', item.userName],
                  ['Email', item.contactEmail],
                  ['Linked Task', item.taskId],
                  ['Linked Website', item.websiteId],
                  ['ATTN', item.tags.filter((tag) => tag.startsWith('ATTN')).join(', ')],
                  ['Attachments', formatNumber(item.metadata.attachmentCount)],
                  ['Reply', plainValue(item.metadata.reply)],
                ]}
                compact
              />
              {item.href && (
                <Button asChild size="sm" variant="outline" className="mt-3">
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    Open Communication
                  </a>
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function BillingTab({
  subscriptions,
  invoices,
  payments,
  proposals,
  timeEntries,
  loading,
  onOpenSubscription,
  onOpenInvoice,
  onOpenPayment,
  onOpenProposal,
  onOpenTimeEntry,
}: {
  subscriptions: ManagedTableRecord[];
  invoices: ManagedTableRecord[];
  payments: ManagedTableRecord[];
  proposals: Proposal[];
  timeEntries: ManagedTableRecord[];
  loading: boolean;
  onOpenSubscription: (id: string) => void;
  onOpenInvoice: (id: string) => void;
  onOpenPayment: (id: string) => void;
  onOpenProposal: (id: string) => void;
  onOpenTimeEntry: (id: string) => void;
}) {
  if (loading) return <PanelLoading />;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-5">
        <SummaryStat label="Subscriptions" value={String(subscriptions.length)} icon={Receipt} />
        <SummaryStat label="Invoices" value={String(invoices.length)} icon={Receipt} />
        <SummaryStat label="Payments" value={String(payments.length)} icon={Receipt} />
        <SummaryStat label="Proposals" value={String(proposals.length)} icon={FileText} />
        <SummaryStat label="Time Entries" value={String(timeEntries.length)} icon={CheckSquare} />
      </div>
      <ManagedRecordSection
        title="Subscriptions"
        configKey="subscriptions"
        records={subscriptions}
        empty="No linked subscriptions."
        onOpen={onOpenSubscription}
      />
      <ManagedRecordSection
        title="Invoices"
        configKey="invoices"
        records={invoices}
        empty="No linked invoices."
        onOpen={onOpenInvoice}
      />
      <ProposalSection proposals={proposals} onOpen={onOpenProposal} />
      <ManagedRecordSection
        title="Payments"
        configKey="payments"
        records={payments}
        empty="No linked payments."
        onOpen={onOpenPayment}
      />
      <ManagedRecordSection
        title="Time Entries"
        configKey="timeEntries"
        records={timeEntries}
        empty="No linked time entries."
        onOpen={onOpenTimeEntry}
      />
    </div>
  );
}

function UsageTab({ client }: { client: Client }) {
  return (
    <Card className="p-4">
      <SectionTitle>Usage / Wallets</SectionTitle>
      <p className="mb-3 text-[12px] text-muted-foreground">
        Wallet data is shown for reference only and is not featured in the Homebase overview.
      </p>
      <FactList
        facts={[
          ['Total Minutes Billable', formatDuration(client.totalMinutesBillable)],
          ['Total Minutes Tracked', formatDuration(client.totalMinutesTracked)],
          ['Voice Agent Licenses', formatNumber(client.voiceAgentLicenses)],
          ['Voice Wallet Records', formatCount(client.walletAiVoiceIds, 'wallet')],
          ['Wallet Records', formatCount(client.walletIds, 'wallet')],
          ['USD Balance Time Tokens', formatCurrency(client.usdBalanceTimeTokens)],
          ['Current Time Balance', formatNumber(client.totalCurrentTimeFromAiVoice)],
        ]}
      />
    </Card>
  );
}

function DriveSection({ client, framed = true }: { client: Client; framed?: boolean }) {
  const [mode, setMode] = useState<DriveMode>('grid');
  const driveUrl = driveFolderUrl(client);
  const embedSrc = driveEmbedSrc(client, mode);
  const content = (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-[18px] font-semibold">Shared Folder & File Uploads</h2>
          <p className="text-[12px] text-muted-foreground">
            This is a public folder. DO NOT upload sensitive documents.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant={mode === 'grid' ? 'default' : 'outline'} onClick={() => setMode('grid')}>
            Grid
          </Button>
          <Button size="sm" variant={mode === 'list' ? 'default' : 'outline'} onClick={() => setMode('list')}>
            List
          </Button>
          {driveUrl && (
            <Button asChild size="sm" variant="outline">
              <a href={driveUrl} target="_blank" rel="noopener noreferrer">
                Open Folder
              </a>
            </Button>
          )}
        </div>
      </div>
      {embedSrc ? (
        <div className="overflow-hidden rounded-lg border border-border bg-background">
          <iframe
            title={`${client.name} shared Drive folder`}
            src={embedSrc}
            className="h-[520px] w-full bg-background"
            loading="lazy"
          />
        </div>
      ) : (
        <EmptyPanel>
          {driveUrl
            ? 'A Google Drive folder URL exists, but the Shared Drive Folder ID is missing. Add the folder ID to enable the embed here.'
            : 'No shared Google Drive folder is connected yet. Add a Shared Drive Folder ID to enable the embed.'}
        </EmptyPanel>
      )}
    </div>
  );

  return framed ? <Card className="p-4">{content}</Card> : content;
}

function ManagedRecordSection({
  title,
  configKey,
  records,
  empty,
  onOpen,
}: {
  title: string;
  configKey: ManagedTableKey;
  records: ManagedTableRecord[];
  empty: string;
  onOpen: (id: string) => void;
}) {
  const config = MANAGED_TABLE_CONFIGS[configKey];
  const columns = config.columns.filter((column) => column.card !== false).slice(0, 6);
  return (
    <div className="space-y-2">
      <SectionTitle>{title}</SectionTitle>
      {records.length === 0 ? (
        <EmptyPanel>{empty}</EmptyPanel>
      ) : (
        <ManagedRecordTable records={records} columns={columns} titleField={config.titleField} onOpen={onOpen} />
      )}
    </div>
  );
}

function ManagedRecordTable({
  records,
  columns,
  titleField,
  onOpen,
}: {
  records: ManagedTableRecord[];
  columns: ManagedTableColumn[];
  titleField: string;
  onOpen: (id: string) => void;
}) {
  const visibleColumns = columns.length > 0 ? columns : [{ id: 'title', label: 'Title', sourceField: titleField }];
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              {visibleColumns.map((column) => (
                <th key={column.id} className="px-3 py-2.5">
                  {column.label}
                </th>
              ))}
              <th className="px-3 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id} className="border-t border-border">
                {visibleColumns.map((column) => (
                  <td key={column.id} className="max-w-[280px] px-3 py-2.5">
                    <FormattedValue value={record.fields[column.sourceField]} column={column} />
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right">
                  <Button size="sm" variant="outline" onClick={() => onOpen(record.id)}>
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProposalSection({ proposals, onOpen }: { proposals: Proposal[]; onOpen: (id: string) => void }) {
  return (
    <div className="space-y-2">
      <SectionTitle>Proposals</SectionTitle>
      {proposals.length === 0 ? (
        <EmptyPanel>No linked proposals.</EmptyPanel>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                <th className="px-3 py-2.5">Proposal</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Amount</th>
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map((proposal) => (
                <tr key={proposal.id} className="border-t border-border">
                  <td className="px-3 py-2.5 font-medium">{proposal.proposalName}</td>
                  <td className="px-3 py-2.5">{proposal.status && <Badge variant="muted">{proposal.status}</Badge>}</td>
                  <td className="px-3 py-2.5">{formatCurrency(proposal.proposalAmount)}</td>
                  <td className="px-3 py-2.5">{formatOptionalDate(proposal.proposalDate)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Button size="sm" variant="outline" onClick={() => onOpen(proposal.id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FormattedValue({ value, column }: { value: unknown; column: ManagedTableColumn }) {
  const text = plainValue(value);
  if (!text) return <span className="text-muted-foreground">-</span>;
  if (column.type === 'status') return <Badge variant="muted">{text}</Badge>;
  if (column.type === 'url') {
    return (
      <a href={normalizeUrl(text)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
        Open Link
      </a>
    );
  }
  if (column.type === 'currency') return <span className="tabular-nums">{formatCurrency(numberValue(value), column)}</span>;
  if (column.type === 'date' || column.type === 'datetime') return <span>{formatOptionalDate(text)}</span>;
  if (column.type === 'duration') return <span>{formatDuration(numberValue(value))}</span>;
  if (column.type === 'image') {
    const src = imageUrlFromValue(value);
    return src ? <img src={src} alt="" className="h-9 w-9 rounded-md object-cover" /> : <span>{text}</span>;
  }
  return <span className="line-clamp-2">{text}</span>;
}

function InfoRow({
  label,
  value,
  href,
  copyValue,
  kind,
  multiline,
  compact,
}: {
  label: string;
  value?: string;
  href?: string;
  copyValue?: string;
  kind?: 'email' | 'phone';
  multiline?: boolean;
  compact?: boolean;
}) {
  const displayHref = href ?? (kind === 'email' && value ? `mailto:${value}` : kind === 'phone' && value ? `tel:${value}` : undefined);
  return (
    <div className={cn('grid gap-2 px-4 py-3 md:grid-cols-[190px_minmax(0,1fr)]', compact && 'md:grid-cols-1')}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">{label}</div>
      <div className={cn('min-w-0 text-[13px] leading-relaxed', !value && 'text-muted-foreground')}>
        {value ? (
          <span className={cn(!multiline && 'truncate')}>
            {displayHref ? (
              <a href={displayHref} target={kind ? undefined : '_blank'} rel="noopener noreferrer" className="text-primary hover:underline">
                {value}
              </a>
            ) : (
              value
            )}
            {copyValue && <CopyButton value={copyValue} label={label} inline />}
          </span>
        ) : (
          'No value'
        )}
      </div>
    </div>
  );
}

function CompactContactRow({
  label,
  value,
  href,
  kind,
}: {
  label: string;
  value?: string;
  href?: string;
  kind?: 'email' | 'phone';
}) {
  if (!value) return null;
  const displayHref = href ?? (kind === 'email' ? `mailto:${value}` : kind === 'phone' ? `tel:${value}` : undefined);
  return (
    <div className="grid gap-1 text-[12px]">
      <span className="font-medium text-muted-foreground">{label}</span>
      {displayHref ? (
        <a
          href={displayHref}
          target={kind ? undefined : '_blank'}
          rel="noopener noreferrer"
          className="break-words text-foreground hover:text-primary hover:underline"
        >
          {value}
        </a>
      ) : (
        <span className="break-words text-foreground">{value}</span>
      )}
    </div>
  );
}

function ExpandableInfoRow({ label, value }: { label: string; value?: string }) {
  const [expanded, setExpanded] = useState(false);
  if (!value) return <InfoRow label={label} value={value} multiline />;
  const long = value.length > 260;
  return (
    <div className="grid gap-2 px-4 py-3 md:grid-cols-[190px_minmax(0,1fr)]">
      <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">{label}</div>
      <div>
        <div className={cn('text-[13px] leading-relaxed', !expanded && long && 'line-clamp-4')}>
          <MarkdownNotes notes={value} emptyState="No value" />
        </div>
        {long && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="mt-2 text-[12px] font-medium text-primary hover:underline"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
}

function ContactChips({ names }: { names?: string[] }) {
  if (!names || names.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {names.slice(0, 8).map((name) => (
        <Badge key={name} variant="outline">
          {name}
        </Badge>
      ))}
      {names.length > 8 && <Badge variant="muted">+{names.length - 8}</Badge>}
    </div>
  );
}

function SummaryStat({ label, value, icon: Icon }: { label: string; value: string; icon: LucideIcon }) {
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
    </Card>
  );
}

function TabHeader({
  title,
  icon: Icon,
  actionLabel,
  onAction,
}: {
  title: string;
  icon?: LucideIcon;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />}
        <h2 className="text-[16px] font-semibold">{title}</h2>
      </div>
      {actionLabel && onAction && (
        <Button size="sm" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

function FactList({ facts, compact = false }: { facts: Array<[string, string | undefined]>; compact?: boolean }) {
  const visible = facts.filter(([, value]) => Boolean(value));
  if (visible.length === 0) return <div className="text-[12px] text-muted-foreground">No values.</div>;
  return (
    <dl className={cn('space-y-2 text-[12px]', compact && 'space-y-1.5')}>
      {visible.map(([label, value]) => (
        <div key={label} className="grid gap-1 sm:grid-cols-[150px_minmax(0,1fr)]">
          <dt className="font-medium text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="space-y-1.5 text-[12px] font-medium">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">{children}</h3>;
}

function EmptyPanel({ children }: { children: ReactNode }) {
  return <Card className="px-4 py-6 text-center text-[13px] text-muted-foreground">{children}</Card>;
}

function PanelLoading() {
  return <Card className="h-36 animate-pulse bg-muted/40" />;
}

function PanelError({ label, error }: { label: string; error: unknown }) {
  return (
    <Card className="px-4 py-3 text-[13px] text-destructive">
      Could not load {label}: {error instanceof Error ? error.message : 'Unknown error'}
    </Card>
  );
}

function Avatar({ name, imageUrl }: { name: string; imageUrl?: string }) {
  if (imageUrl) return <img src={imageUrl} alt="" className="h-12 w-12 rounded-md object-cover" />;
  return (
    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-[13px] font-semibold text-primary">
      {initials(name) || <User className="h-5 w-5" aria-hidden />}
    </div>
  );
}

function CopyButton({ value, label, inline = false }: { value: string; label: string; inline?: boolean }) {
  const setNotice = useUiStore((state) => state.setNotice);
  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      onClick={() => {
        navigator.clipboard.writeText(value).then(
          () => setNotice(`${label} copied`),
          () => setNotice(`Could not copy ${label}`, 'destructive'),
        );
      }}
      className={cn(
        'inline-flex items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground',
        inline ? 'ml-2 p-1 align-middle' : 'px-2 py-1.5',
      )}
    >
      <Copy className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

function driveFolderUrl(client: Client): string | undefined {
  if (client.googleDriveFolderUrl) return client.googleDriveFolderUrl;
  if (!client.sharedDriveFolderId) return undefined;
  return `https://drive.google.com/drive/folders/${encodeURIComponent(client.sharedDriveFolderId)}`;
}

function driveEmbedSrc(client: Client, mode: DriveMode): string | undefined {
  if (!client.sharedDriveFolderId) return undefined;
  const suffix = mode === 'grid' ? 'grid' : 'list';
  return `https://drive.google.com/embeddedfolderview?id=${encodeURIComponent(client.sharedDriveFolderId)}#${suffix}`;
}

function normalizeUrl(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function organizationAddress(client: Client): string | undefined {
  if (client.businessFullAddress) return client.businessFullAddress;
  const street = [client.businessStreetAddress, client.businessStreetAddress2].filter(Boolean).join(', ');
  const region = [client.businessState, client.businessZip].filter(Boolean).join(' ');
  const cityLine = [client.businessCity, region].filter(Boolean).join(', ');
  return [street, cityLine].filter(Boolean).join(', ') || undefined;
}

function setupWarnings(client: Client, subscriptionCount: number): string[] {
  const warnings: string[] = [];
  if (!client.logoUrl) warnings.push('No logo');
  if (!client.sharedDriveFolderId) warnings.push('No Shared Drive Folder ID');
  if (!client.designatedBillingContactId && !client.billingContactName) warnings.push('No billing contact');
  if (!client.website) warnings.push('No website');
  if (subscriptionCount === 0) warnings.push('No active subscription');
  if (!client.webmasterIds?.length && !client.webmasterEmails?.length) warnings.push('No webmaster assigned');
  return warnings;
}

function isClosedTask(task: Task): boolean {
  if (task.semanticStatus === 'done') return true;
  const status = task.status.toLowerCase();
  return ['closed', 'done', 'complete', 'completed', 'archived', 'cancelled', 'canceled'].some((token) =>
    status.includes(token),
  );
}

function managedField(record: ManagedTableRecord, sourceField: string): string | undefined {
  return plainValue(record.fields[sourceField]);
}

function imageUrlFromValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return undefined;
  const first = value[0] as
    | {
        url?: string;
        thumbnails?: { large?: { url?: string }; small?: { url?: string }; full?: { url?: string } };
      }
    | undefined;
  return first?.thumbnails?.large?.url ?? first?.thumbnails?.full?.url ?? first?.thumbnails?.small?.url ?? first?.url;
}

function plainValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) {
    const joined = value.map(plainValue).filter(Boolean).join(', ');
    return joined || undefined;
  }
  if (typeof value === 'object') {
    const record = value as { value?: unknown; filename?: unknown; url?: unknown };
    return plainValue(record.value) ?? plainValue(record.filename) ?? plainValue(record.url);
  }
  return undefined;
}

function numberValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value)) return numberValue(value[0]);
  const text = plainValue(value);
  if (!text) return undefined;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatOptionalDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return formatRelativeDate(value);
}

function formatCurrency(value: number | undefined, column?: ManagedTableColumn): string | undefined {
  if (value === undefined) return undefined;
  const dollars = column?.currencyUnit === 'cents' ? value / 100 : value;
  return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(value: unknown): string | undefined {
  const number = numberValue(value);
  if (number !== undefined) return number.toLocaleString();
  return plainValue(value);
}

function formatCount(ids: string[] | undefined, singular: string): string | undefined {
  if (!ids || ids.length === 0) return undefined;
  return ids.length === 1 ? `1 ${singular}` : `${ids.length} ${singular}s`;
}

function countRecords(ids: string[] | undefined): number {
  return ids?.length ?? 0;
}

function hasUsageData(client: Client): boolean {
  return Boolean(
    client.totalMinutesBillable ||
      client.totalMinutesTracked ||
      client.voiceAgentLicenses ||
      client.walletAiVoiceIds?.length ||
      client.walletIds?.length ||
      client.usdBalanceTimeTokens ||
      client.totalCurrentTimeFromAiVoice,
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}
